import { formatObservationValue } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { preferImperialQuantity } from './quantity';

export const BP_PANEL_CODE = '85354-9';
export const BP_SYSTOLIC_CODE = '8480-6';
export const BP_DIASTOLIC_CODE = '8462-4';

const UCUM_DISPLAY: Record<string, string> = {
  // Survey scores are unitless for display — an empty string makes formatVitalValue render the bare number
  '{score}': '',
  score: '',
  // Heart rate. `bpm` reads as "beats per minute" clinically, so only beats map to it —
  // never respiration or the bare `/min`, which would misread as a heart rate.
  '{beats}/min': 'bpm',
  'beats/min': 'bpm',
  // Respiratory rate — distinct from `bpm` to avoid being misread as a heart rate.
  '{breaths}/min': 'br/min',
  'breaths/min': 'br/min',
  'mm[Hg]': 'mmHg',
  Cel: '°C',
  '[degF]': '°F',
  '[lb_av]': 'lbs',
  kg: 'kg',
  '[in_us]': 'in',
  cm: 'cm',
  'kg/m2': 'kg/m²',
  '%': '%',
};

/** Maps a UCUM code to a human-readable display string, returning the code unchanged if unknown. */
export const toVitalUnitDisplay = (ucumCode: string): string => UCUM_DISPLAY[ucumCode] ?? ucumCode;

/**
 * LOINC codes whose minute-valued quantities represent a sleep duration: the nightly total plus the
 * REM / light / deep stage durations. These render as hours-and-minutes ("5h 30m") rather than bare
 * minutes ("330 min"), which reads more naturally for sleep.
 */
const SLEEP_DURATION_LOINC_CODES = new Set(['93832-4', '93829-0', '93830-8', '93831-6']);

/** Whether a LOINC code is a sleep-duration metric (nightly total or a REM/light/deep stage). */
export const isSleepDurationCode = (code: string | undefined): boolean =>
  code !== undefined && SLEEP_DURATION_LOINC_CODES.has(code);

/** Whether an observation is a sleep-duration reading (nightly total or a REM/light/deep stage). Its
 * quantity value is minutes and renders as hours-and-minutes ("5h 30m"). */
export const isSleepDurationObservation = (obs: Observation): boolean =>
  obs.code?.coding?.some((coding) => isSleepDurationCode(coding.code)) ?? false;

/**
 * Units that denote an hours-valued quantity rather than minutes. A quantity in one of these units is
 * multiplied by 60 to normalize it to minutes before a minutes-based formatter or comparison.
 *
 * Sleep *readings* are now stored in minutes (`min`) by every producer, so this is only used for sleep
 * *targets*, which the lifestyle-measurement catalog authors in hours (`hours/night`, e.g. code
 * `93832-4` in `apps/medplum-config/src/valueSet/lifestyleMeasurements.ts`). `formatTarget` and native's
 * `targetValueInMinutes` use it to convert those hour-valued targets. If a new hours-valued unit is
 * added there, add it here too.
 */
const HOUR_UNITS = new Set(['hours/night', 'hours', 'hour', 'hr', 'h']);

/** Whether a unit (or code) string denotes an hours-valued quantity rather than minutes. */
export const isHourUnit = (unit: string | undefined): boolean => HOUR_UNITS.has((unit ?? '').toLowerCase());

const MINUTE_UNITS = new Set(['min', 'minute', 'minutes']);

/** Whether a unit string denotes minutes — such a series is a duration and reads best as hours +
 * minutes (via `formatSleepDuration`) rather than a bare number. */
export const isMinutesUnit = (unit: string | undefined): boolean => MINUTE_UNITS.has((unit ?? '').trim().toLowerCase());

/** Normalize a duration to minutes, converting hour-valued units. Used for sleep targets (authored in
 * hours); readings are already stored in minutes and don't need this. */
export const durationToMinutes = (value: number, unit: string | undefined, code: string | undefined): number =>
  isHourUnit(unit ?? code) ? value * 60 : value;

/**
 * Split a total-minutes sleep duration into whole hours and the remaining minutes — the shape both the
 * web hours+minutes entry fields and the "5h 30m" display formatter need. Negative and fractional
 * inputs are clamped and rounded, since sleep is stored as whole non-negative minutes.
 */
export const splitSleepMinutes = (totalMinutes: number): { hours: number; minutes: number } => {
  const total = Math.max(0, Math.round(totalMinutes));
  return { hours: Math.floor(total / 60), minutes: total % 60 };
};

/** Combine entered hours and minutes into a total-minutes sleep duration. */
export const sleepMinutes = (hours: number | undefined, minutes: number | undefined): number =>
  (hours ?? 0) * 60 + (minutes ?? 0);

/**
 * Whether entered hours/minutes form a valid sleep duration: whole numbers, minutes ∈ [0, 59], and a
 * total ∈ (0, 24h] — no single day holds more than 24 hours of sleep. Shared by patient-web's
 * `RecordMeasurementForm` and patient-native's `validateManualEntry` so both gate on the same rules.
 */
export const isValidSleepDuration = (hours: number | undefined, minutes: number | undefined): boolean => {
  const h = hours ?? 0;
  const m = minutes ?? 0;
  if (!Number.isInteger(h) || !Number.isInteger(m)) return false;
  if (h < 0 || m < 0 || m > 59) return false;
  const total = sleepMinutes(h, m);
  return total > 0 && total <= 24 * 60;
};

/**
 * Render a sleep duration given in minutes as hours and minutes — "5h 30m", "6h", "45m". Sub-hour
 * durations drop the hours; whole-hour durations drop the minutes. Minutes are rounded to the nearest
 * integer since sleep quantities are stored as whole minutes.
 */
export const formatSleepDuration = (minutes: number): string => {
  const { hours, minutes: mins } = splitSleepMinutes(minutes);
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

/**
 * Render a quantity value for display, stripping binary floating-point noise from unit-converted
 * readings (e.g. a mmol/L glucose stored as 106.20000000000002 mg/dL renders "106.2"). Rounding to
 * 12 significant digits removes the noise — which only appears beyond that — without clobbering the
 * genuine precision of small-magnitude lab values (0.055 stays 0.055). When `localeString` is given
 * the cleaned value is grouped for that locale (10000 → "10,000" for en, "10.000" for es); the high
 * `maximumFractionDigits` keeps `Intl` from re-rounding the already-cleaned value.
 */
export const formatVitalNumber = (value: number, localeString?: string): string => {
  const cleaned = Number(value.toPrecision(12));
  return localeString
    ? new Intl.NumberFormat(localeString, { maximumFractionDigits: 20 }).format(cleaned)
    : String(cleaned);
};

/**
 * Split an observation value into its numeric part and unit, so callers can style them
 * separately (e.g. a smaller unit suffix). Produces `{ value: "110/80", unit: "mmHg" }` for
 * composite BP observations. `unit` is undefined when there is none (or for the text fallback).
 * Pass `localeString` (from `useDateLocale().localeString`) for locale-aware number grouping; when
 * omitted the number is rendered bare so it matches whatever the caller shows elsewhere.
 */
/**
 * Format a single quantity into display parts. Sleep durations read more naturally as "5h 30m" — the
 * h/m carry the unit, so there's no separate suffix; every other metric pairs the number with its unit.
 */
const quantityParts = (
  obs: Observation,
  value: number,
  unit: string | undefined,
  code: string | undefined,
  localeString?: string,
): { value: string; unit?: string } =>
  isSleepDurationObservation(obs)
    ? { value: formatSleepDuration(value) }
    : { value: formatVitalNumber(value, localeString), unit: toVitalUnitDisplay(unit ?? code ?? '') || undefined };

/** The imperial-preferred quantity of the component carrying `code`, or undefined when absent. */
const componentQuantity = (obs: Observation, code: string): ReturnType<typeof preferImperialQuantity> =>
  preferImperialQuantity(
    obs.component?.find((c) => c.code?.coding?.some((coding) => coding.code === code))?.valueQuantity,
  );

/** "110/80 mmHg" parts for a composite BP observation, or undefined when it isn't one. */
const bloodPressureParts = (obs: Observation, localeString?: string): { value: string; unit?: string } | undefined => {
  const systolic = componentQuantity(obs, BP_SYSTOLIC_CODE);
  const diastolic = componentQuantity(obs, BP_DIASTOLIC_CODE);
  if (systolic?.value === undefined || diastolic?.value === undefined) return undefined;
  // Systolic and diastolic are always co-unitized (mm[Hg]), so the systolic unit represents the pair;
  // the diastolic unit is intentionally not read.
  return {
    value: `${formatVitalNumber(systolic.value, localeString)}/${formatVitalNumber(diastolic.value, localeString)}`,
    unit: toVitalUnitDisplay(systolic.unit ?? systolic.code ?? '') || undefined,
  };
};

export const formatVitalValueParts = (
  obs: Observation,
  unknown = 'Unknown',
  localeString?: string,
  code?: string,
): { value: string; unit?: string } => {
  // When a specific result code is requested and it lives in a `component` rather than the top-level
  // value — a sleep-stage duration (deep/light/REM) on a panel whose top-level value is the night
  // total — format that component. Several stage Tasks share one panel Observation, so each must read
  // its own stage, not the total. A `code` matching the top-level falls through to the normal path.
  if (code) {
    const requested = componentQuantity(obs, code);
    if (requested?.value !== undefined) {
      return quantityParts(obs, requested.value, requested.unit, requested.code, localeString);
    }
  }
  const qty = preferImperialQuantity(obs.valueQuantity);
  if (qty?.value !== undefined) {
    return quantityParts(obs, qty.value, qty.unit, qty.code, localeString);
  }
  return bloodPressureParts(obs, localeString) ?? { value: formatObservationValue(obs) ?? unknown };
};

/** Format an observation value for display, producing "110/80 mmHg" for composite BP observations.
 * Pass `localeString` (from `useDateLocale()`) for locale-aware number grouping. Pass `code` to read a
 * specific `component` value (a sleep stage) instead of the panel's top-level total. */
export const formatVitalValue = (
  obs: Observation,
  unknown = 'Unknown',
  localeString?: string,
  code?: string,
): string => {
  const { value, unit } = formatVitalValueParts(obs, unknown, localeString, code);
  return unit ? `${value} ${unit}` : value;
};
